<template>
  <div class="space-geometry-viz">
    <div class="viz-header-premium" v-latex>
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="main-layout horizontal">
      <!-- 3D 渲染画布 (SVG 投影) -->
      <div class="canvas-3d-wrapper">
        <svg width="100%" height="420" viewBox="0 0 600 420" preserveAspectRatio="xMidYMid meet">
          <!-- 坐标系 -->
          <g transform="translate(300, 240)">
            <defs>
               <marker id="arr-x" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#94a3b8" /></marker>
            </defs>
            <line x1="-200" y1="100" x2="200" y2="-100" stroke="#475569" stroke-width="1.5" stroke-dasharray="2,2" /> <!-- x 轴 -->
            <line x1="-250" y1="0" x2="250" y2="0" stroke="#475569" stroke-width="1.5" /> <!-- y 轴 -->
            <line x1="0" y1="180" x2="0" y2="-180" stroke="#475569" stroke-width="1.5" /> <!-- z 轴 -->
            
            <text x="210" y="-105" fill="#64748b" font-size="12">x轴</text>
            <text x="260" y="5" fill="#64748b" font-size="12">y轴</text>
            <text x="5" y="-185" fill="#64748b" font-size="12">z轴</text>

            <!-- A. 多面体与球模式 -->
            <g v-if="mode === 'solid'">
               <g v-if="subMode === 'sphere'">
                  <circle cx="0" cy="0" :r="radius" fill="url(#sphereGrad)" stroke="#3b82f6" stroke-width="2" />
                  <ellipse cx="0" cy="0" :rx="radius" :ry="radius/3" fill="none" stroke="rgba(59, 130, 246, 0.4)" stroke-dasharray="4,2" />
                  <line x1="0" y1="0" :x2="radius" y2="0" stroke="#fbbf24" stroke-width="2" />
                  <text :x="radius/2" y="-10" fill="#fbbf24" font-size="14" font-weight="900">R</text>
               </g>
               <g v-else>
                  <!-- 长方体 3D 投影 -->
                  <path :d="boxPath" fill="rgba(6, 182, 212, 0.1)" stroke="#06b6d4" stroke-width="2" />
                  <line :x1="boxV[4].x" :y1="boxV[4].y" :x2="boxV[5].x" :y2="boxV[5].y" stroke="#06b6d4" stroke-width="2" />
                  <line :x1="boxV[5].x" :y1="boxV[5].y" :x2="boxV[6].x" :y2="boxV[6].y" stroke="#06b6d4" stroke-width="2" />
                  <line :x1="boxV[6].x" :y1="boxV[6].y" :x2="boxV[7].x" :y2="boxV[7].y" stroke="#0891b2" stroke-width="1" stroke-dasharray="4,2" />
               </g>
            </g>

            <!-- B. 空间向量与角度模式 -->
            <g v-else-if="mode === 'vector'">
               <!-- 法向量 n -->
               <line x1="0" y1="0" :x2="nv.x" :y2="nv.y" stroke="#f43f5e" stroke-width="4" marker-end="url(#arr-n)" />
               <text :x="nv.x+5" :y="nv.y-5" fill="#f43f5e" font-size="14" font-weight="900">法向量 n</text>
               
               <!-- 空间平面的投影线 -->
               <path d="M -150,50 L 50,150 L 250,-50 L 50,-150 Z" fill="rgba(244, 63, 94, 0.05)" stroke="rgba(244, 63, 94, 0.4)" stroke-width="1.5" />
               
               <!-- 方向向量 v -->
               <line x1="0" y1="0" :x2="vv.x" :y2="vv.y" stroke="#10b981" stroke-width="4" marker-end="url(#arr-v)" />
               <text :x="vv.x+5" :y="vv.y+20" fill="#10b981" font-size="14" font-weight="900">方向向量 v</text>
            </g>
          </g>

          <defs>
            <radialGradient id="sphereGrad" cx="30%" cy="30%" r="70%">
                <stop offset="0%" stop-color="#60a5fa" stop-opacity="0.3" />
                <stop offset="100%" stop-color="#1e3a8a" stop-opacity="0.1" />
            </radialGradient>
            <marker id="arr-n" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#f43f5e" /></marker>
            <marker id="arr-v" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#10b981" /></marker>
          </defs>
        </svg>

        <div class="hud-panel-premium">
           <div class="hud-group">
              <label>测量指标</label>
              <div class="hud-val" v-if="mode === 'solid'">
                 {{ subMode === 'sphere' ? '球体积 V ≈ ' + (4/3*Math.PI*Math.pow(radius/20,3)).toFixed(1) : '多面体体积 V = abc' }}
              </div>
              <div class="hud-val" v-else>
                 {{ '线面角 θ = ' + angle.toFixed(1) + '°' }}
              </div>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="controls-premium">
        <div class="control-card">
           <div class="c-title">模式切换</div>
           <el-radio-group v-model="mode" size="mini" class="wide-radio">
              <el-radio-button label="solid">多面体与球</el-radio-button>
              <el-radio-button label="vector">向量法与空间角</el-radio-button>
           </el-radio-group>
           
           <template v-if="mode === 'solid'">
              <div class="mt-4">
                <label class="s-label">子类</label>
                <el-radio-group v-model="subMode" size="mini">
                   <el-radio-button label="box">长方体</el-radio-button>
                   <el-radio-button label="sphere">球体</el-radio-button>
                </el-radio-group>
              </div>
              <div class="mt-4">
                 <label class="s-label">参数调节 (大小/比例)</label>
                 <el-slider v-model="radius" :min="40" :max="120" size="small"></el-slider>
              </div>
           </template>

           <template v-else>
              <div class="mt-4">
                 <label class="s-label">向量方向控制 (θ₁)</label>
                 <el-slider v-model="vx" :min="-120" :max="120" size="small"></el-slider>
                 <label class="s-label">法向控制 (θ₂)</label>
                 <el-slider v-model="vy" :min="-120" :max="120" size="small"></el-slider>
              </div>
           </template>
        </div>

        <div class="discovery-card-premium">
           <div class="d-icon"><i class="el-icon-position"></i></div>
           <div class="d-text">
              <strong>教学重点：</strong>{{ discoveryText }}
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SpaceGeometryViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'solid',
      subMode: 'box',
      radius: 80,
      vx: 80,
      vy: -60
    };
  },
  watch: {
    knowledgePoint: {
      handler(val) { this.detectMode(val); },
      immediate: true
    }
  },
  computed: {
    topicTitle() {
      if (this.mode === 'vector') return '向量法：解决空间角与距离问题的通用模型';
      return this.subMode === 'sphere' ? '球体：空间中点集的轨迹与几何特征' : '多面体：空间结构的顶点、棱与面';
    },
    activeFormula() {
       if (this.mode === 'vector') return 'sinθ = |n·v| / (|n||v|)';
       if (this.subMode === 'sphere') return 'V = 4/3πR³ | S = 4πR²';
       return 'V = Sh | 欧拉公式: V-E+F=2';
    },
    angle() {
       // Mock angle calculation based on sliders
       return Math.abs(this.vx * 0.5 + this.vy * 0.2) % 90;
    },
    nv() { return { x: 0, y: -120 }; },
    vv() { return { x: this.vx, y: this.vy }; },
    boxPath() {
       const v = this.boxV;
       return `M ${v[0].x},${v[0].y} L ${v[1].x},${v[1].y} L ${v[2].x},${v[2].y} L ${v[3].x},${v[3].y} Z 
               M ${v[0].x},${v[0].y} L ${v[4].x},${v[4].y} 
               M ${v[1].x},${v[1].y} L ${v[5].x},${v[5].y} 
               M ${v[2].x},${v[2].y} L ${v[6].x},${v[6].y}`;
    },
    boxV() {
       const r = this.radius;
       // Projection matrix mock
       return [
          { x: -r, y: -r/2 }, { x: r, y: -r/2 }, { x: r, y: r/2 }, { x: -r, y: r/2 }, // Front
          { x: -r+30, y: -r/2-30 }, { x: r+30, y: -r/2-30 }, { x: r+30, y: r/2-30 }, { x: -r+30, y: r/2-30 } // Back
       ];
    },
    discoveryText() {
       if (this.mode === 'vector') return '向量法将空间想象转化为代数计算。通过平面的法向量 n 与直线方向向量 v 的夹角，可以精确求得线面角、二面角等难点指标。';
       return '从平面几何到立体几何，核心在于维度的递增。多面体的基本元素是点线面，而球体则是到中心距离固定的完美集锦。';
    }
  },
  methods: {
    detectMode(kp) {
       const val = (kp || '').toString();
       if (/向量|角度|法/.test(val)) {
         this.mode = 'vector';
       } else {
         this.mode = 'solid';
         if (/球/.test(val)) this.subMode = 'sphere';
         else this.subMode = 'box';
       }
    }
  }
}
</script>

<style scoped lang="scss">
.space-geometry-viz { flex: 1; display: flex; flex-direction: column; gap: 10px; height: 100%; overflow: hidden; background: #fff; }
.viz-header-premium { display: flex; justify-content: space-between; align-items: center; padding: 4px 10px; .brief { font-size: 15px; color: #0f172a; font-weight: 800; } .formula-chip { background: #f1f5f9; color: #475569; border: 1.5px solid #e2e8f0; padding: 6px 16px; border-radius: 8px; font-weight: 900; font-family: "Cascadia Code", monospace; font-size: 15px; } }
.main-layout { display: flex; gap: 12px; flex: 1; }
.canvas-3d-wrapper { flex: 1.2; background: #020617; border-radius: 16px; position: relative; border: 2px solid #1e293b; overflow: hidden; }
.controls-premium { flex: 0.8; display: flex; flex-direction: column; gap: 12px; overflow-y: auto; }
.control-card { background: #f8fafc; padding: 18px; border-radius: 16px; border: 1px solid #e2e8f0; .c-title { font-size: 12px; color: #64748b; font-weight: 900; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 1px; } .wide-radio { width: 100%; display: flex; ::v-deep .el-radio-button { flex: 1; span { width: 100%; } } } }
.hud-panel-premium { position: absolute; top: 20px; left: 20px; background: rgba(15, 23, 42, 0.85); border-left: 4px solid #3b82f6; border-radius: 0 12px 12px 0; padding: 10px 18px; .hud-group { label { font-size: 10px; color: #94a3b8; font-weight: 800; display: block; margin-bottom: 3px; } .hud-val { color: #fff; font-size: 14px; font-weight: 900; font-family: monospace; } } }
.discovery-card-premium { background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 16px; padding: 15px; display: flex; gap: 12px; .d-icon { color: #0369a1; font-size: 1.4rem; } .d-text { font-size: 12px; color: #0c4a6e; line-height: 1.6; strong { color: #0369a1; font-weight: 900; } } }
.s-label { display: block; font-size: 12px; color: #64748b; font-weight: 800; margin: 12px 0 5px 0; }
.mt-4 { margin-top: 15px; }
</style>
